import { useState, useEffect, useMemo } from 'react';
import { 
  HelpCircle, 
  Variable, 
  List, 
  Sparkles, 
  Search, 
  ChevronRight, 
  Phone, 
  Calendar, 
  Mail, 
  MessageSquare, 
  User, 
  Tag, 
  FileText, 
  Webhook, 
  Play,
  Settings,
  X,
  Copy,
  Check
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-frontend/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@easyflow/ui-frontend/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@easyflow/ui-frontend/components/ui/popover';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@easyflow/ui-frontend/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';

// Sentinel used to round-trip empty-string option values through Radix Select,
// which forbids `<SelectItem value="">`. Some node configs use '' to mean
// "Tutti / Default / Non cambiare", so we map it bidirectionally without
// touching the persisted config value.
const EMPTY_OPTION_SENTINEL = '__empty_option__';

export type FieldMode = 'fixed' | 'all' | 'variable';
export type VariableValueMode = 'inherited' | 'select' | 'input';

export interface VariableFieldValue {
  variablePath: string;
  valueMode: VariableValueMode;
  value?: string;
}

export interface FieldOption {
  value: string;
  label: string;
}

export interface AvailableVariable {
  path: string;
  type: string;
  context?: string; // Semantic context (phone_number, trunk_id, email, etc.)
  source: string;
  source_node_id?: string;
  source_node_label?: string;
  // Legacy CRM grouping key sent by some backends alongside source_node_label.
  sourceName?: string;
  description?: string;
}

export interface ConfigFieldWithModeProps {
  label: string;
  fieldKey: string;
  // Dynamic node-config field value: string in practice (variables serialize
  // to '{{...}}'), but a numeric config value can arrive from the JSON payload.
  value: string | number | null | undefined;
  onChange: (value: string, mode: FieldMode) => void;
  mode?: FieldMode;
  onModeChange?: (mode: FieldMode) => void;
  tooltip?: string;
  description?: string; // Detailed description of what this field does
  format?: string; // Expected format (e.g., "E.164 (+39123456789)", "UUID", "email@example.com")
  options?: FieldOption[];
  optionsSource?: string; // API endpoint for dynamic options (e.g., '/api/workflows/options/trunks')
  loadOptions?: () => Promise<FieldOption[]>;
  availableVariables?: AvailableVariable[];
  compatibleTypes?: string[];
  compatibleContexts?: string[]; // Filter variables by context (phone_number, trunk_id, etc.)
  fieldContext?: string; // The context this field expects
  allowAll?: boolean;
  allLabel?: string;
  placeholder?: string;
  inputType?: 'text' | 'number' | 'textarea';
  required?: boolean;
  className?: string;
}

const getNodeIcon = (nodeType: string) => {
  if (nodeType.includes('call')) return Phone;
  if (nodeType.includes('scheduled') || nodeType.includes('schedule')) return Calendar;
  if (nodeType.includes('email') || nodeType.includes('mail')) return Mail;
  if (nodeType.includes('sms') || nodeType.includes('whatsapp') || nodeType.includes('message')) return MessageSquare;
  if (nodeType.includes('lead') || nodeType.includes('customer') || nodeType.includes('user')) return User;
  if (nodeType.includes('tag')) return Tag;
  if (nodeType.includes('form')) return FileText;
  if (nodeType.includes('webhook')) return Webhook;
  if (nodeType.includes('manual')) return Play;
  if (nodeType.includes('voicemail')) return Phone;
  return Settings;
};

const getSourceColor = (source: string) => {
  switch (source) {
    case 'trigger':
      return {
        bg: 'bg-purple-100 dark:bg-purple-900/40',
        text: 'text-purple-700 dark:text-purple-300',
        border: 'border-purple-200 dark:border-purple-700',
        icon: 'text-purple-500'
      };
    case 'node':
      return {
        bg: 'bg-green-100 dark:bg-green-900/40',
        text: 'text-green-700 dark:text-green-300',
        border: 'border-green-200 dark:border-green-700',
        icon: 'text-green-500'
      };
    case 'system':
      return {
        bg: 'bg-blue-100 dark:bg-blue-900/40',
        text: 'text-blue-700 dark:text-blue-300',
        border: 'border-blue-200 dark:border-blue-700',
        icon: 'text-blue-500'
      };
    case 'crm':
      return {
        bg: 'bg-amber-100 dark:bg-amber-900/40',
        text: 'text-amber-700 dark:text-amber-300',
        border: 'border-amber-200 dark:border-amber-700',
        icon: 'text-amber-500'
      };
    default:
      return {
        bg: 'bg-gray-100 dark:bg-gray-800',
        text: 'text-gray-700 dark:text-gray-300',
        border: 'border-gray-200 dark:border-gray-700',
        icon: 'text-gray-500'
      };
  }
};

const getSourceLabel = (source: string) => {
  switch (source) {
    case 'trigger': return 'Trigger';
    case 'node': return 'Nodo';
    case 'system': return 'Sistema';
    case 'crm': return 'CRM';
    default: return source;
  }
};

const getTypeLabel = (type: string) => {
  switch (type) {
    case 'string': return 'testo';
    case 'number': return 'numero';
    case 'boolean': return 'bool';
    case 'date': return 'data';
    case 'object': return 'oggetto';
    case 'array': return 'lista';
    default: return type;
  }
};

const getContextLabel = (context: string) => {
  const labels: Record<string, string> = {
    phone_number: 'telefono',
    phone_pattern: 'pattern tel.',
    email: 'email',
    email_subject: 'oggetto email',
    contact_name: 'nome contatto',
    first_name: 'nome',
    last_name: 'cognome',
    extension_number: 'interno',
    trunk_id: 'ID trunk',
    trunk_name: 'nome trunk',
    call_id: 'ID chiamata',
    call_direction: 'direzione',
    call_disposition: 'esito',
    duration_seconds: 'durata (sec)',
    datetime: 'data/ora',
    message_id: 'ID messaggio',
    message_content: 'contenuto msg',
    conversation_id: 'ID conversazione',
    lead_id: 'ID lead',
    lead_title: 'titolo lead',
    lead_source: 'fonte lead',
    customer_id: 'ID cliente',
    person_id: 'ID persona',
    pipeline_id: 'ID pipeline',
    campaign_id: 'ID campagna',
    stage_id: 'ID stage',
    form_id: 'ID form',
    template_id: 'ID template',
    tag_name: 'nome tag',
    user_id: 'ID utente',
    user_name: 'nome utente',
    url: 'URL',
    file_path: 'percorso file',
    workflow_id: 'ID workflow',
    monetary_value: 'valore monetario',
    priority_level: 'priorità',
    vat_number: 'P. IVA',
    tax_code: 'codice fiscale',
    notes: 'note',
    generic_string: 'testo generico',
    generic_number: 'numero generico',
    generic_object: 'oggetto generico',
    boolean_flag: 'flag',
  };
  return labels[context] || context;
};

// Context compatibility rules - maps targetContext to compatible sourceContexts
const contextCompatibility: Record<string, string[]> = {
  // Phone/Communication
  phone_number: ['phone_number', 'phone_pattern', 'generic_string'],
  phone_pattern: ['phone_pattern', 'phone_number', 'generic_string'],
  extension_number: ['extension_number', 'generic_string'],
  
  // Call-related
  trunk_id: ['trunk_id', 'generic_string'],
  trunk_name: ['trunk_name', 'generic_string'],
  call_id: ['call_id', 'generic_string'],
  call_direction: ['call_direction', 'generic_string'],
  call_disposition: ['call_disposition', 'generic_string'],
  duration_seconds: ['duration_seconds', 'generic_number'],
  voicemail_box_id: ['voicemail_box_id', 'generic_string'],
  
  // Time
  datetime: ['datetime'],
  
  // Messaging
  message_id: ['message_id', 'generic_string'],
  message_content: ['message_content', 'generic_string', 'notes', 'lead_title', 'email_subject'],
  template_id: ['template_id', 'generic_string'],
  email_subject: ['email_subject', 'generic_string', 'lead_title', 'message_content'],
  notes: ['notes', 'message_content', 'generic_string'],
  
  // Contact
  email: ['email', 'generic_string'],
  contact_name: ['contact_name', 'first_name', 'last_name', 'user_name', 'generic_string'],
  
  // CRM Entities
  lead_id: ['lead_id', 'generic_string'],
  lead_title: ['lead_title', 'generic_string', 'message_content'],
  lead_source: ['lead_source', 'generic_string'],
  customer_id: ['customer_id', 'generic_string'],
  person_id: ['person_id', 'lead_id', 'customer_id', 'generic_string'],
  campaign_id: ['campaign_id', 'generic_string'],
  stage_id: ['stage_id', 'generic_string'],
  pipeline_id: ['pipeline_id', 'generic_string'],
  form_id: ['form_id', 'generic_string'],
  tag_name: ['tag_name', 'generic_string'],
  conversation_id: ['conversation_id', 'generic_string'],
  phone_number_id: ['phone_number_id', 'generic_string'],
  whatsapp_sender_id: ['whatsapp_sender_id', 'phone_number_id', 'generic_string'],
  
  // User
  user_id: ['user_id', 'generic_string'],
  user_name: ['user_name', 'contact_name', 'generic_string'],
  
  // System
  workflow_id: ['workflow_id', 'generic_string'],
  run_id: ['run_id', 'generic_string'],
  
  // Financial
  monetary_value: ['monetary_value', 'generic_number'],
  vat_number: ['vat_number', 'tax_code', 'generic_string'],
  tax_code: ['tax_code', 'vat_number', 'generic_string'],
  priority_level: ['priority_level', 'generic_number'],
  
  // Generic fallbacks
  generic_string: ['generic_string', 'phone_number', 'email', 'contact_name', 'message_content', 'url', 'file_path', 'notes', 'lead_title', 'extension_number'],
  generic_number: ['generic_number', 'duration_seconds', 'percentage', 'monetary_value', 'priority_level'],
  generic_object: ['generic_object', 'lead_object', 'entity_object', 'form_data', 'utm_data'],
};

// Category registry: explicit groupings to avoid heuristic substring matches
const contextCategories: Record<string, string> = {
  // IDs - grouped by entity type
  lead_id: 'crm_id', customer_id: 'crm_id', person_id: 'crm_id',
  campaign_id: 'campaign_id', form_id: 'form_id', pipeline_id: 'pipeline_id', stage_id: 'pipeline_id',
  user_id: 'user_id', 
  call_id: 'call_id', voicemail_id: 'voicemail_id', voicemail_message_id: 'voicemail_id',
  trunk_id: 'trunk_id', message_id: 'message_id', conversation_id: 'message_id',
  task_id: 'task_id', workflow_id: 'workflow_id', notification_id: 'notification_id',
  phone_number_id: 'phone_id', whatsapp_sender_id: 'phone_id',
  form_submission_id: 'form_id', subworkflow_run_id: 'workflow_id',
  template_id: 'template_id', whatsapp_template_id: 'template_id',
  external_id: 'external_id', entity_id: 'entity_id',
  
  // Names
  first_name: 'person_name', last_name: 'person_name', contact_name: 'person_name', user_name: 'person_name',
  lead_title: 'title', task_title: 'title',
  campaign_name: 'entity_name', pipeline_name: 'entity_name', stage_name: 'entity_name',
  trunk_name: 'entity_name', form_name: 'entity_name', company_name: 'entity_name', tag_name: 'entity_name',
  webhook_name: 'entity_name', variable_name: 'variable_ref',
  
  // Contact info
  phone_number: 'phone', phone_pattern: 'phone', extension_number: 'extension',
  email: 'email', email_list: 'email_list',
  address: 'address', city: 'address', province: 'address', postal_code: 'address',
  
  // Content/Text
  message_content: 'text_content', notes: 'text_content', task_description: 'text_content',
  notification_title: 'notification_text', notification_message: 'notification_text',
  email_subject: 'email_text', email_body: 'email_text',
  log_message: 'log_text', template_text: 'template_content', formatted_text: 'text_content',
  ai_prompt: 'ai_text', ai_system_prompt: 'ai_text', ai_response: 'ai_text',
  
  // URLs
  url: 'url', url_params: 'url', file_path: 'path',
  
  // Time
  datetime: 'datetime', target_datetime: 'datetime',
  duration_seconds: 'duration', duration_milliseconds: 'duration', delay_value: 'duration',
  timeout_seconds: 'duration', schedule_frequency: 'schedule', cron: 'schedule', time_unit: 'time_unit', timezone: 'timezone',
  
  // Numbers
  monetary_value: 'money', percentage: 'percentage', priority_level: 'priority',
  token_count: 'counter', token_limit: 'counter', retry_attempt: 'counter', loop_index: 'counter',
  confidence_score: 'score', sentiment_score: 'score', ai_temperature: 'ai_param',
  http_status_code: 'http_code', generic_number: 'generic_number', branch_id: 'branch',
  
  // Booleans
  boolean_flag: 'boolean',
  
  // Objects/Arrays
  generic_object: 'generic_object', generic_array: 'generic_array', entity_object: 'entity_object',
  form_data: 'form_object', utm_data: 'utm_object', log_data: 'log_object',
  http_headers: 'http_object', request_body: 'http_object', response_body: 'http_object',
  loop_item: 'iterator', iterable_array: 'iterator', branch_results: 'branch_object',
  error_object: 'error', error_message: 'error',
  workflow_input: 'workflow_data', workflow_output: 'workflow_data',
  tag_list: 'list', field_list: 'list', user_id_list: 'list',
  transform_input: 'transform', transform_expression: 'transform',
  
  // Enums/Types
  call_direction: 'call_enum', call_disposition: 'call_enum', call_type: 'call_enum', hangup_cause: 'call_enum',
  media_type: 'media_enum', customer_type: 'crm_enum', customer_status: 'crm_enum', customer_source: 'crm_enum',
  contact_status: 'crm_enum', lead_source: 'crm_enum', business_sector: 'crm_enum',
  http_method: 'http_enum', log_level: 'log_enum', notification_type: 'notification_enum',
  ai_model: 'ai_enum', transform_operation: 'transform_enum', comparison_operator: 'operator_enum',
  workflow_status: 'workflow_enum', sentiment_label: 'sentiment_enum', entity_type: 'entity_enum',
  
  // Conditions/Logic
  condition_operand: 'condition', switch_value: 'condition', matched_value: 'condition',
  goto_target: 'node_ref', variable_value: 'variable_value', keyword_match: 'keyword', keyword_pattern: 'keyword',
  
  // UTM
  utm_source: 'utm', utm_medium: 'utm', utm_campaign: 'utm', utm_term: 'utm', utm_content: 'utm',
  
  // Misc
  ip_address: 'ip', user_agent: 'user_agent', tax_code: 'tax', vat_number: 'tax',
  email_message_id: 'email_id', generic_string: 'generic_string',
};

const isContextCompatible = (sourceContext: string | undefined, targetContext: string | undefined): boolean => {
  // No context defined = allow any
  if (!sourceContext || !targetContext) return true;
  
  // Exact match always allowed
  if (sourceContext === targetContext) return true;
  
  // Check explicit compatibility rules first (whitelist - highest priority)
  const compatible = contextCompatibility[targetContext];
  if (compatible) {
    return compatible.includes(sourceContext);
  }
  
  // Check category-based compatibility using explicit registry
  const sourceCategory = contextCategories[sourceContext];
  const targetCategory = contextCategories[targetContext];
  
  // Same category = compatible
  if (sourceCategory && targetCategory && sourceCategory === targetCategory) {
    return true;
  }
  
  // Different categories = block
  if (sourceCategory && targetCategory && sourceCategory !== targetCategory) {
    return false;
  }
  
  // If target not in any registry (unmapped), allow to avoid blocking new contexts
  // This is a safe fallback - new contexts work by default
  if (!targetCategory && !compatible) {
    return true;
  }
  
  // Default: block
  return false;
};

export function ConfigFieldWithMode({
  label,
  fieldKey,
  value,
  onChange,
  mode: externalMode,
  onModeChange,
  tooltip,
  description,
  format,
  options: staticOptions,
  optionsSource,
  loadOptions,
  availableVariables = [],
  compatibleTypes,
  compatibleContexts,
  fieldContext,
  allowAll = true,
  allLabel = 'Qualsiasi',
  placeholder,
  inputType = 'text',
  required = false,
  className,
}: ConfigFieldWithModeProps) {
  const [internalMode, setInternalMode] = useState<FieldMode>('fixed');
  const [dynamicOptions, setDynamicOptions] = useState<FieldOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [variablePopoverOpen, setVariablePopoverOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [copiedPath, setCopiedPath] = useState<string | null>(null);
  const [variableValueMode, setVariableValueMode] = useState<VariableValueMode>('inherited');
  const [variableValue, setVariableValue] = useState<string>('');

  // Parse variable field value from stored format
  // Format: "{{path}}" for inherited, "{{path|select:value}}" for select, "{{path|input:value}}" for input
  const parseVariableValue = (val: unknown): { path: string; mode: VariableValueMode; value: string } | null => {
    if (!val || typeof val !== 'string' || !val.startsWith('{{') || !val.endsWith('}}')) return null;
    const inner = val.slice(2, -2);
    const pipeIndex = inner.indexOf('|');
    if (pipeIndex === -1) {
      return { path: inner, mode: 'inherited', value: '' };
    }
    const path = inner.slice(0, pipeIndex);
    const modeValue = inner.slice(pipeIndex + 1);
    const colonIndex = modeValue.indexOf(':');
    if (colonIndex === -1) {
      return { path, mode: modeValue as VariableValueMode, value: '' };
    }
    const parsedMode = modeValue.slice(0, colonIndex) as VariableValueMode;
    const parsedValue = modeValue.slice(colonIndex + 1);
    return { path, mode: parsedMode, value: parsedValue };
  };

  // Serialize variable field value to storage format
  const serializeVariableValue = (path: string, vMode: VariableValueMode, val: string): string => {
    if (vMode === 'inherited') {
      return `{{${path}}}`;
    }
    return `{{${path}|${vMode}:${val}}}`;
  };

  // Initialize variableValueMode and variableValue from stored value
  useEffect(() => {
    const parsed = parseVariableValue(value);
    if (parsed) {
      setVariableValueMode(parsed.mode);
      setVariableValue(parsed.value);
    }
  }, [value]);
  
  const mode = externalMode ?? internalMode;
  const setMode = (newMode: FieldMode) => {
    if (onModeChange) {
      onModeChange(newMode);
    } else {
      setInternalMode(newMode);
    }
  };

  // Load options from API if optionsSource is provided
  useEffect(() => {
    const fetchOptions = async () => {
      if (optionsSource && dynamicOptions.length === 0) {
        setLoading(true);
        try {
          // apiClient gestisce prefix slug + rotte tenant-agnostic centralmente
          // (TENANT_AGNOSTIC_PREFIXES in lib/api.ts): niente template /api/${slug}.
          const endpoint = optionsSource.startsWith('/api/') ? optionsSource.substring(4) : optionsSource;
          const res = await apiClient.get<{ options?: Array<{ value: string; label: string }> }>(endpoint);
          if (res.success && res.data) {
            setDynamicOptions(res.data.options || []);
          }
        } catch (error) {
          console.error('Error loading options from', optionsSource, error);
        } finally {
          setLoading(false);
        }
      } else if (loadOptions && dynamicOptions.length === 0) {
        setLoading(true);
        loadOptions()
          .then(opts => setDynamicOptions(opts))
          .catch(console.error)
          .finally(() => setLoading(false));
      }
    };
    fetchOptions();
  }, [optionsSource, loadOptions, dynamicOptions.length]);

  const options = staticOptions || dynamicOptions;

  const [showAllCrm, setShowAllCrm] = useState(false);

  const filteredVariables = useMemo(() => {
    let vars = availableVariables;
    
    // Filter by type if compatibleTypes specified
    if (compatibleTypes && compatibleTypes.length > 0) {
      vars = vars.filter(v => compatibleTypes.includes(v.type) || compatibleTypes.includes('any'));
    }
    
    // Context filtering: apply only to CRM variables
    // Trigger, node, and system variables always pass through (they're workflow-calculated)
    if (fieldContext) {
      vars = vars.filter(v => {
        if (v.source !== 'crm') return true;
        if (showAllCrm) return true;
        return isContextCompatible(v.context, fieldContext);
      });
    } else if (compatibleContexts && compatibleContexts.length > 0) {
      vars = vars.filter(v => {
        if (v.source !== 'crm') return true;
        if (showAllCrm) return true;
        return compatibleContexts.includes(v.context || '') || 
          compatibleContexts.includes('any');
      });
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      vars = vars.filter(v => 
        v.path.toLowerCase().includes(query) ||
        v.description?.toLowerCase().includes(query) ||
        v.source_node_label?.toLowerCase().includes(query) ||
        v.context?.toLowerCase().includes(query)
      );
    }
    
    return vars;
  }, [availableVariables, compatibleTypes, compatibleContexts, fieldContext, searchQuery, showAllCrm]);

  const groupedVariables = useMemo(() => {
    const groups: Record<string, { 
      label: string; 
      source: string; 
      nodeType: string;
      variables: AvailableVariable[] 
    }> = {};
    
    filteredVariables.forEach(v => {
      // For CRM variables, group by sourceName (category from database)
      // For other sources, group by source_node_id or source
      let groupKey: string;
      let groupLabel: string;
      
      if (v.source === 'crm') {
        // Use source_node_label as category (e.g., 'Cliente', 'Lead', 'Chiamate')
        // Backend sends category name in source_node_label for CRM variables
        const categoryName = v.source_node_label || v.sourceName || 'CRM';
        groupKey = `crm_${categoryName}`;
        groupLabel = categoryName;
      } else {
        groupKey = v.source_node_id || v.source;
        groupLabel = v.source_node_label || getSourceLabel(v.source);
      }
      
      const group = groups[groupKey] ?? (groups[groupKey] = {
        label: groupLabel,
        source: v.source,
        nodeType: v.source_node_id || v.source,
        variables: []
      });
      group.variables.push(v);
    });
    
    return groups;
  }, [filteredVariables]);

  const isVariableValue = typeof value === 'string' && value.startsWith('{{') && value.endsWith('}}');

  const getSelectedVariableInfo = () => {
    if (!isVariableValue) return null;
    // Parse the path (handles both simple {{path}} and extended {{path|mode:value}} format)
    const parsed = parseVariableValue(value);
    if (!parsed) return null;
    return availableVariables.find(v => v.path === parsed.path);
  };

  const handleModeSwitch = (newMode: FieldMode) => {
    setMode(newMode);
    if (newMode === 'all') {
      onChange('*', newMode);
    } else if (newMode === 'fixed' && isVariableValue) {
      onChange('', newMode);
    } else if (newMode === 'variable') {
      setVariablePopoverOpen(true);
    }
  };

  const handleVariableSelect = (varPath: string) => {
    // When selecting a new variable, default to inherited mode
    setVariableValueMode('inherited');
    setVariableValue('');
    onChange(`{{${varPath}}}`, 'variable');
    setVariablePopoverOpen(false);
    setSearchQuery('');
  };

  const handleFixedChange = (newValue: string) => {
    onChange(newValue, 'fixed');
  };

  const handleClearVariable = () => {
    setMode('fixed');
    setVariableValueMode('inherited');
    setVariableValue('');
    onChange('', 'fixed');
  };

  // Get the variable path from current value
  const getVariablePath = (): string | null => {
    const parsed = parseVariableValue(value);
    return parsed?.path || null;
  };

  // Handle change of variable value mode (inherited/select/input)
  const handleVariableValueModeChange = (newValueMode: VariableValueMode) => {
    setVariableValueMode(newValueMode);
    const path = getVariablePath();
    if (!path) return;
    
    if (newValueMode === 'inherited') {
      setVariableValue('');
      onChange(`{{${path}}}`, 'variable');
    } else {
      // Keep current value or empty
      const newValue = variableValue || '';
      onChange(serializeVariableValue(path, newValueMode, newValue), 'variable');
    }
  };

  // Handle change of variable value (the actual value for select/input modes)
  const handleVariableValueChange = (newVal: string) => {
    setVariableValue(newVal);
    const path = getVariablePath();
    if (!path) return;
    onChange(serializeVariableValue(path, variableValueMode, newVal), 'variable');
  };

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const copyToClipboard = (path: string) => {
    navigator.clipboard.writeText(`{{${path}}}`);
    setCopiedPath(path);
    setTimeout(() => setCopiedPath(null), 1500);
  };

  useEffect(() => {
    if (variablePopoverOpen && Object.keys(groupedVariables).length > 0) {
      setExpandedGroups(new Set(Object.keys(groupedVariables)));
    }
  }, [variablePopoverOpen, groupedVariables]);

  const selectedVarInfo = getSelectedVariableInfo();

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {label}
          </Label>
          {required && (
            <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4 ml-1">Obbligatorio</Badge>
          )}
          {(tooltip || description || format) && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-sm p-3">
                  <div className="space-y-2">
                    {(description || tooltip) && (
                      <p className="text-sm text-popover-foreground">{description || tooltip}</p>
                    )}
                    {format && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-text-muted">Formato:</span>
                        <code className="text-xs rounded bg-surface px-1.5 py-0.5 font-mono text-primary">{format}</code>
                      </div>
                    )}
                    {required && (
                      <div className="flex items-center gap-1">
                        <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Obbligatorio</Badge>
                      </div>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-md p-0.5">
          <Button
            type="button"
            size="sm"
            variant={mode === 'fixed' ? 'secondary' : 'ghost'}
            className={cn(
              "h-6 px-2 text-xs",
              mode === 'fixed' && "bg-white dark:bg-gray-700 shadow-sm"
            )}
            onClick={() => handleModeSwitch('fixed')}
          >
            <List className="w-3 h-3 mr-1" />
            Fisso
          </Button>
          
          {allowAll && (
            <Button
              type="button"
              size="sm"
              variant={mode === 'all' ? 'secondary' : 'ghost'}
              className={cn(
                "h-6 px-2 text-xs",
                mode === 'all' && "bg-white dark:bg-gray-700 shadow-sm"
              )}
              onClick={() => handleModeSwitch('all')}
            >
              <Sparkles className="w-3 h-3 mr-1" />
              {allLabel}
            </Button>
          )}
          
          {availableVariables.length > 0 && (
            <Popover open={variablePopoverOpen} onOpenChange={(open) => { setVariablePopoverOpen(open); if (!open) setShowAllCrm(false); }}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant={mode === 'variable' ? 'secondary' : 'ghost'}
                  className={cn(
                    "h-6 px-2 text-xs",
                    mode === 'variable' && "bg-white dark:bg-gray-700 shadow-sm"
                  )}
                  onClick={() => {
                    if (mode !== 'variable') {
                      handleModeSwitch('variable');
                    } else {
                      setVariablePopoverOpen(true);
                    }
                  }}
                >
                  <Variable className="w-3 h-3 mr-1" />
                  Variabile
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0" sideOffset={5}>
                <div className="p-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Seleziona variabile
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Usa un valore dinamico dal flusso
                    </p>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Cerca variabile..."
                      className="pl-8 h-8 text-sm bg-gray-50 dark:bg-gray-900"
                    />
                  </div>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  <div className="p-2">
                    {Object.keys(groupedVariables).length === 0 ? (
                      <div className="text-center py-6">
                        <Variable className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                        <p className="text-sm text-gray-500">
                          {searchQuery ? 'Nessun risultato trovato' : 'Nessuna variabile disponibile'}
                        </p>
                      </div>
                    ) : (
                      <>
                        {Object.entries(groupedVariables).map(([groupKey, group]) => {
                          const IconComponent = getNodeIcon(group.nodeType);
                          const colors = getSourceColor(group.source);
                          const isExpanded = expandedGroups.has(groupKey);
                          
                          return (
                            <Collapsible 
                              key={groupKey} 
                              open={isExpanded}
                              onOpenChange={() => toggleGroup(groupKey)}
                            >
                              <CollapsibleTrigger className="w-full">
                                <div className={cn(
                                  "flex items-center justify-between p-2 rounded-md transition-colors",
                                  "hover:bg-gray-100 dark:hover:bg-gray-800"
                                )}>
                                  <div className="flex items-center gap-2">
                                    <div className={cn("p-1 rounded", colors.bg)}>
                                      <IconComponent className={cn("w-3.5 h-3.5", colors.icon)} />
                                    </div>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                      {group.label}
                                    </span>
                                    <Badge variant="outline" className="text-xs px-1.5 py-0">
                                      {group.variables.length}
                                    </Badge>
                                  </div>
                                  <ChevronRight className={cn(
                                    "w-4 h-4 text-gray-400 transition-transform",
                                    isExpanded && "rotate-90"
                                  )} />
                                </div>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <div className="ml-4 pl-3 border-l-2 border-gray-200 dark:border-gray-700 space-y-0.5 pb-2">
                                  {group.variables.map((variable, idx) => (
                                    <button
                                      key={`${variable.path}-${idx}`}
                                      type="button"
                                      className={cn(
                                        "w-full text-left p-2 rounded-md transition-colors group/var",
                                        "hover:bg-gray-100 dark:hover:bg-gray-800"
                                      )}
                                      onClick={() => handleVariableSelect(variable.path)}
                                    >
                                      <div className="flex items-center justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <code className="text-xs font-mono text-purple-600 dark:text-purple-400 truncate">
                                              {variable.path.split('.').pop()}
                                            </code>
                                            <span className="text-[10px] text-gray-400 uppercase">
                                              {getTypeLabel(variable.type)}
                                            </span>
                                            {variable.context && (
                                              <span className="text-[9px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-full">
                                                {getContextLabel(variable.context)}
                                              </span>
                                            )}
                                          </div>
                                          {variable.description && (
                                            <p className="text-xs text-gray-500 mt-0.5 truncate">
                                              {variable.description}
                                            </p>
                                          )}
                                        </div>
                                        <button
                                          type="button"
                                          className="opacity-0 group-hover/var:opacity-100 p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-all"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            copyToClipboard(variable.path);
                                          }}
                                        >
                                          {copiedPath === variable.path ? (
                                            <Check className="w-3.5 h-3.5 text-green-500" />
                                          ) : (
                                            <Copy className="w-3.5 h-3.5 text-gray-400" />
                                          )}
                                        </button>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              </CollapsibleContent>
                            </Collapsible>
                          );
                        })}
                        {fieldContext && !showAllCrm && !searchQuery.trim() && (
                          <button
                            type="button"
                            className="w-full text-center py-2 mt-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                            onClick={() => setShowAllCrm(true)}
                          >
                            Mostra tutte le variabili CRM
                          </button>
                        )}
                        {showAllCrm && fieldContext && (
                          <button
                            type="button"
                            className="w-full text-center py-2 mt-1 text-xs text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md transition-colors"
                            onClick={() => setShowAllCrm(false)}
                          >
                            Mostra solo variabili pertinenti
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      <div>
        {mode === 'all' ? (
          <div className="flex items-center gap-2 p-2.5 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-md">
            <Sparkles className="w-4 h-4 text-purple-500" />
            <span className="text-sm text-purple-700 dark:text-purple-300">
              {allLabel} (nessun filtro applicato)
            </span>
          </div>
        ) : mode === 'variable' && isVariableValue ? (
          <div className="space-y-3">
            {/* Variable selector row */}
            <div className={cn(
              "flex items-center gap-2 p-2.5 rounded-md border",
              selectedVarInfo ? getSourceColor(selectedVarInfo.source).bg : 'bg-blue-50 dark:bg-blue-900/20',
              selectedVarInfo ? getSourceColor(selectedVarInfo.source).border : 'border-blue-200 dark:border-blue-800'
            )}>
              <Variable className={cn(
                "w-4 h-4 flex-shrink-0",
                selectedVarInfo ? getSourceColor(selectedVarInfo.source).icon : 'text-blue-500'
              )} />
              <div className="flex-1 min-w-0">
                <code className={cn(
                  "text-sm font-mono",
                  selectedVarInfo ? getSourceColor(selectedVarInfo.source).text : 'text-blue-700 dark:text-blue-300'
                )}>
                  {`{{${getVariablePath() || ''}}}`}
                </code>
                {selectedVarInfo?.description && (
                  <p className="text-xs text-gray-500 truncate mt-0.5">
                    {selectedVarInfo.description}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setVariablePopoverOpen(true)}
                >
                  Cambia
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={handleClearVariable}
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            
            {/* Value mode selector */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Valore della variabile:</Label>
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-md p-0.5">
                <Button
                  type="button"
                  size="sm"
                  variant={variableValueMode === 'inherited' ? 'secondary' : 'ghost'}
                  className={cn(
                    "h-6 px-2 text-xs flex-1",
                    variableValueMode === 'inherited' && "bg-white dark:bg-gray-700 shadow-sm"
                  )}
                  onClick={() => handleVariableValueModeChange('inherited')}
                >
                  Ereditato
                </Button>
                {options && options.length > 0 && (
                  <Button
                    type="button"
                    size="sm"
                    variant={variableValueMode === 'select' ? 'secondary' : 'ghost'}
                    className={cn(
                      "h-6 px-2 text-xs flex-1",
                      variableValueMode === 'select' && "bg-white dark:bg-gray-700 shadow-sm"
                    )}
                    onClick={() => handleVariableValueModeChange('select')}
                  >
                    Select
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant={variableValueMode === 'input' ? 'secondary' : 'ghost'}
                  className={cn(
                    "h-6 px-2 text-xs flex-1",
                    variableValueMode === 'input' && "bg-white dark:bg-gray-700 shadow-sm"
                  )}
                  onClick={() => handleVariableValueModeChange('input')}
                >
                  Input
                </Button>
              </div>
              
              {/* Value input based on mode */}
              {variableValueMode === 'inherited' ? (
                <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                  <Sparkles className="w-4 h-4 text-green-500" />
                  <span className="text-xs text-green-700 dark:text-green-300">
                    Il valore sarà ereditato dal flusso
                  </span>
                </div>
              ) : variableValueMode === 'select' && options && options.length > 0 ? (() => {
                const hasEmptyOption = options.some(o => String(o.value) === '');
                return (
                  <Select
                    value={hasEmptyOption && variableValue === '' ? EMPTY_OPTION_SENTINEL : (variableValue || '')}
                    onValueChange={(v) => handleVariableValueChange(hasEmptyOption && v === EMPTY_OPTION_SENTINEL ? '' : v)}
                    disabled={loading}
                  >
                    <SelectTrigger className="bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700">
                      <SelectValue placeholder={loading ? 'Caricamento...' : (placeholder || 'Seleziona valore...')} />
                    </SelectTrigger>
                    <SelectContent>
                      {options.map(opt => {
                        const safeValue = String(opt.value) === '' ? EMPTY_OPTION_SENTINEL : String(opt.value);
                        return (
                          <SelectItem key={safeValue} value={safeValue}>
                            {opt.label}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                );
              })()
              : variableValueMode === 'input' ? (
                inputType === 'number' ? (
                  <Input
                    type="number"
                    value={variableValue || ''}
                    onChange={(e) => handleVariableValueChange(e.target.value)}
                    placeholder={placeholder || 'Inserisci valore...'}
                    className="bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700"
                  />
                ) : inputType === 'textarea' ? (
                  <textarea
                    value={variableValue || ''}
                    onChange={(e) => handleVariableValueChange(e.target.value)}
                    placeholder={placeholder || 'Inserisci valore...'}
                    rows={3}
                    className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500"
                  />
                ) : (
                  <Input
                    type="text"
                    value={variableValue || ''}
                    onChange={(e) => handleVariableValueChange(e.target.value)}
                    placeholder={placeholder || 'Inserisci valore...'}
                    className="bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700"
                  />
                )
              ) : null}
            </div>
          </div>
        ) : mode === 'variable' ? (
          <div className="flex gap-2">
            <Input
              type="text"
              value={value || ''}
              onChange={(e) => onChange(e.target.value, 'variable')}
              placeholder="Digita {{variabile}} o seleziona dal picker..."
              className="flex-1 bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 font-mono text-sm"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-shrink-0"
              onClick={() => setVariablePopoverOpen(true)}
            >
              <Variable className="w-4 h-4 mr-1" />
              Scegli
            </Button>
          </div>
        ) : options && options.length > 0 ? (() => {
          const hasEmptyOption = options.some(o => String(o.value) === '');
          return (
            <Select
              value={hasEmptyOption && value === '' ? EMPTY_OPTION_SENTINEL : ((value as string | undefined) || '')}
              onValueChange={(v) => handleFixedChange(hasEmptyOption && v === EMPTY_OPTION_SENTINEL ? '' : v)}
              disabled={loading}
            >
              <SelectTrigger className="bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700">
                <SelectValue placeholder={loading ? 'Caricamento...' : (placeholder || `Seleziona ${label.toLowerCase()}...`)} />
              </SelectTrigger>
              <SelectContent>
                {options.map(opt => {
                  const safeValue = String(opt.value) === '' ? EMPTY_OPTION_SENTINEL : String(opt.value);
                  return (
                    <SelectItem key={safeValue} value={safeValue}>
                      {opt.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          );
        })()
        : inputType === 'number' ? (
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => handleFixedChange(e.target.value)}
            placeholder={placeholder}
            className="bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700"
          />
        ) : inputType === 'textarea' ? (
          <textarea
            value={value || ''}
            onChange={(e) => handleFixedChange(e.target.value)}
            placeholder={placeholder}
            rows={3}
            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500"
          />
        ) : (
          <Input
            type="text"
            value={value || ''}
            onChange={(e) => handleFixedChange(e.target.value)}
            placeholder={placeholder}
            className="bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700"
          />
        )}
      </div>
    </div>
  );
}
